import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowLeft, Shield, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import tamLogo from '@/assets/tam-logo.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [error, setError] = useState('');

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [sending2FA, setSending2FA] = useState(false);

  const { signIn, signInWithMagicLink, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !show2FA) {
      navigate('/dashboard');
    }
  }, [user, navigate, show2FA]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { error } = await signIn(email, password);

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }

    // Check if 2FA is enabled for this user
    try {
      const { data } = await supabase.functions.invoke('two-factor-auth', {
        body: { action: 'check' },
      });

      if (data?.enabled) {
        // Send OTP via reauthentication email
        setSending2FA(true);
        const { error: reAuthError } = await supabase.auth.reauthenticate();
        setSending2FA(false);
        if (reAuthError) {
          console.error('Reauthenticate error:', reAuthError);
          toast.error('Failed to send verification code. Please try again.');
          // Sign out so user can't bypass 2FA
          await supabase.auth.signOut();
          setLoading(false);
          return;
        }
        setShow2FA(true);
        setLoading(false);
        toast.info('A verification code has been sent to your email');
        return;
      }
    } catch {
      // If 2FA check fails, proceed without 2FA
    }

    setLoading(false);
    navigate('/dashboard');
  };

  const handleVerify2FA = async () => {
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setVerifying2FA(true);
    setError('');

    try {
      // Verify the reauthentication nonce by attempting a user update
      const { error: verifyError } = await supabase.auth.updateUser({
        data: { last_2fa_verified: new Date().toISOString() },
        nonce: otpCode,
      });

      if (verifyError) {
        setError('Invalid or expired code. Please try again.');
        setOtpCode('');
        setVerifying2FA(false);
        return;
      }

      toast.success('Verification successful!');
      setShow2FA(false);
      navigate('/dashboard');
    } catch {
      setError('Verification failed. Please try again.');
      setOtpCode('');
    }
    setVerifying2FA(false);
  };

  const handleResendCode = async () => {
    setSending2FA(true);
    setError('');
    try {
      const { error } = await supabase.auth.reauthenticate();
      if (error) throw error;
      toast.success('New code sent to your email');
    } catch {
      toast.error('Failed to resend code');
    }
    setSending2FA(false);
  };

  const handleCancel2FA = async () => {
    await supabase.auth.signOut();
    setShow2FA(false);
    setOtpCode('');
    setError('');
  };

  const handleMagicLinkSignIn = async () => {
    if (!email) {
      setError('Please enter your email first');
      return;
    }

    setMagicLinkLoading(true);
    setError('');

    const { error: magicLinkError } = await signInWithMagicLink(email);
    if (magicLinkError) {
      setError(magicLinkError.message);
      setMagicLinkLoading(false);
      return;
    }

    toast.success('Magic link sent. Check your inbox.');
    setMagicLinkLoading(false);
  };

  // 2FA Verification Screen
  if (show2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8 relative overflow-hidden bg-background">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/3 rounded-full blur-[120px] pointer-events-none" />

        <div className="w-full max-w-md animate-fade-in relative z-10">
          <div className="rounded-2xl border border-border/50 bg-card/80 backdrop-blur-xl p-8 shadow-2xl shadow-primary/5">
            <div className="flex flex-col items-center mb-8">
              <div className="p-4 rounded-2xl bg-primary/10 mb-4">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold">Two-Factor Authentication</h1>
              <p className="text-muted-foreground text-sm mt-1.5 text-center">
                Enter the 6-digit code sent to <span className="font-medium text-foreground">{email}</span>
              </p>
            </div>

            {error && (
              <div className="mb-6 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-destructive flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="flex justify-center mb-6">
              <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button
              onClick={handleVerify2FA}
              className="w-full h-11 rounded-xl font-semibold text-sm mb-3"
              disabled={verifying2FA || otpCode.length !== 6}
            >
              {verifying2FA ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </div>
              ) : (
                'Verify & Sign In'
              )}
            </Button>

            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResendCode}
                disabled={sending2FA}
                className="text-xs text-muted-foreground"
              >
                {sending2FA ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Resend code
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel2FA}
                className="text-xs text-muted-foreground"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 relative overflow-hidden bg-background">
      {/* Ambient glow */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/3 rounded-full blur-[120px] pointer-events-none" />

      <div className="w-full max-w-md animate-fade-in relative z-10">
        <div className="rounded-2xl border border-border/50 bg-card/80 backdrop-blur-xl p-8 shadow-2xl shadow-primary/5">
          {/* Header */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative mb-5">
              <img
                src={tamLogo}
                alt="TAM Tools"
                className="h-14 w-14 rounded-full border border-border/50"
              />
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl opacity-50" />
            </div>
            <h1 className="text-2xl font-bold">
              TAM <span className="text-primary">Store</span>
            </h1>
            <p className="text-muted-foreground text-sm mt-1.5">Sign in to your account</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-destructive flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-accent/30 border-border/50 rounded-xl focus:border-primary/50 transition-colors"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 bg-accent/30 border-border/50 rounded-xl focus:border-primary/50 transition-colors"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:shadow-primary/25"
              disabled={loading || magicLinkLoading}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  {sending2FA ? 'Sending code...' : 'Signing in...'}
                </div>
              ) : (
                'Sign In'
              )}
            </Button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-11 rounded-xl font-semibold text-sm"
              onClick={handleMagicLinkSignIn}
              disabled={loading || magicLinkLoading}
            >
              {magicLinkLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending magic link...
                </div>
              ) : (
                'Send Magic Link'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link to="/signup" className="text-primary hover:underline font-medium">
              Sign up
            </Link>
          </div>

          <div className="mt-5 flex justify-center">
            <Link
              to="/"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
